import * as THREE from 'three';
import { DynamicObject, MovementState, UpdatePriority } from './DynamicObject';
import { LODLevel } from '../../optimization/LODSystem';
import { BoundingBox, BoundingVolume } from '../../physics/BoundingVolume';
import { CollisionSystem } from '../../physics/CollisionSystem';
import { GameState } from '../../game/GameState';
import { StaticObjectType } from '../static/LODStaticObject';
import { GameObject, ObjectType } from '../GameObject';
import { Damageable } from '../../projectiles/ProjectileManager';

/**
 * Base class for a dynamic toaster object with Level of Detail support
 */
export class LODToaster extends DynamicObject implements Damageable {
  // Mesh properties
  protected geometry: THREE.BoxGeometry;
  protected material: THREE.MeshStandardMaterial;
  protected baseScale: THREE.Vector3;
  protected movementSpeed: number;
  
  // LOD-specific geometries and materials
  protected highDetailGeometry: THREE.BoxGeometry;
  protected mediumDetailGeometry: THREE.BoxGeometry;
  protected lowDetailGeometry: THREE.BoxGeometry;
  protected activeDetailLevel: LODLevel = LODLevel.HIGH;
  
  // Collision control
  protected lastCollisionTime: number = 0;
  protected collisionCooldown: number = 0.5; // seconds between collision responses
  
  // Pathfinding properties
  protected loafTargetPosition: THREE.Vector3 = new THREE.Vector3();
  protected pathfindingDirection: THREE.Vector3 = new THREE.Vector3();
  protected readonly pathfindingCooldown: number = 1.0; // seconds between recalculating path
  protected lastPathUpdateTime: number = 0;
  protected readonly steeringStrength: number = 0.8; // How strongly to steer toward goal (0-1)
  protected reachedTarget: boolean = false;

  // Lifecycle management
  protected activeState: boolean = true;
  protected goalReached: boolean = false;
  protected onGoalReachedCallback: (() => void) | null = null;
  protected onDestroyCallback: (() => void) | null = null;
  
  // Health system
  protected maxHealth: number = 100;
  protected currentHealth: number = 100;
  protected isDead: boolean = false;

  constructor(name: string, baseScale: THREE.Vector3, movementSpeed: number) {
    super(name);

    // Set base properties
    this.baseScale = baseScale;
    this.movementSpeed = movementSpeed;

    // Create geometries with different detail levels
    this.highDetailGeometry = new THREE.BoxGeometry(1, 1, 1, 8, 8, 8); // High detail
    this.mediumDetailGeometry = new THREE.BoxGeometry(1, 1, 1, 4, 4, 4); // Medium detail
    this.lowDetailGeometry = new THREE.BoxGeometry(1, 1, 1, 2, 2, 2); // Low detail
    
    // Start with high detail
    this.geometry = this.highDetailGeometry;
    
    // Create red cube material
    this.material = new THREE.MeshStandardMaterial({
      color: 0xFF0000, // Red color
      roughness: 0.7,
      metalness: 0.2
    });

    // Create mesh
    const mesh = new THREE.Mesh(this.geometry, this.material);
    
    // Enable shadows
    mesh.castShadow = true;      // The toaster will cast shadows
    mesh.receiveShadow = true;   // The toaster can receive shadows
    
    this.setMesh(mesh);
    
    console.log(`Created mesh for toaster ${this.getId()}`);

    // Apply the base scale
    this.setScale(baseScale.x, baseScale.y, baseScale.z);

    // Enable LOD support for this object
    this.enableLOD();

    // Add a bounding box scaled to match the object's size
    this.createBoundingVolume();

    // Register with the collision system
    CollisionSystem.getInstance().registerObject(this);

    // Set initial target-seeking movement
    this.updatePathfindingDirection();
    
    // Initialize health based on size (scaled by base scale volume)
    this.initializeHealth();
  }
  
  /**
   * Initialize health based on toaster size
   */
  protected initializeHealth(): void {
    // Calculate volume (rough approximation based on scale)
    const volume = this.baseScale.x * this.baseScale.y * this.baseScale.z;
    
    // Set max health based on volume (larger toasters have more health)
    this.maxHealth = Math.round(100 * volume);
    this.currentHealth = this.maxHealth;
    
    console.log(`Initialized toaster ${this.getId()} with ${this.maxHealth} health`);
  }

  /**
   * Take damage from projectiles or other sources
   * @param amount The amount of damage to take
   */
  public takeDamage(amount: number): void {
    console.log(`Toaster ${this.getId()} took ${amount} damage`);
    // Skip if already dead or inactive
    if (this.isDead || !this.activeState) {
      return;
    }
    
    // Calculate new health
    this.currentHealth -= amount;
    console.log(`Toaster ${this.getId()} took ${amount} damage, health: ${this.currentHealth}/${this.maxHealth}`);
    
    // Update visual feedback - change color based on health percentage
    this.updateHealthVisuals();
    
    // Check if dead
    if (this.currentHealth <= 0) {
      this.die();
    }
  }
  
  /**
   * Update visuals based on health percentage
   */
  protected updateHealthVisuals(): void {
    const mesh = this.getMesh();
    if (!mesh) return;
    
    // Calculate health percentage
    const healthPercent = this.currentHealth / this.maxHealth;
    
    // Change color based on health (red -> yellow -> green)
    const material = (mesh as THREE.Mesh).material as THREE.MeshStandardMaterial;
    
    // Start with red (low health) and interpolate to green (full health)
    if (healthPercent > 0.5) {
      // Yellow to green (more health)
      const g = 1.0;
      const r = 0.5 + (1.0 - healthPercent) * 0.5 * 2.0; // Map 0.5-1.0 to 1.0-0.5
      material.color.setRGB(r, g, 0);
    } else {
      // Red to yellow (less health)
      const r = 1.0;
      const g = healthPercent * 2.0; // Map 0-0.5 to 0-1.0
      material.color.setRGB(r, g, 0);
    }
  }
  
  /**
   * Handle death of the toaster
   */
  protected die(): void {
    console.log(`Toaster ${this.getId()} has been destroyed!`);
    
    this.isDead = true;
    this.activeState = false;
    
    // Visual feedback - make red and fade out
    const mesh = this.getMesh();
    if (mesh) {
      const material = (mesh as THREE.Mesh).material as THREE.MeshStandardMaterial;
      material.color.setRGB(0.7, 0, 0); // Dark red
      material.opacity = 0.5;
      material.transparent = true;
    }
    
    // Notify via callback if set
    if (this.onDestroyCallback) {
      this.onDestroyCallback();
    }
    
    // Deactivate to remove from game
    this.deactivate();
    
    // Unregister from collision system
    CollisionSystem.getInstance().unregisterObject(this);
  }
  
  /**
   * Get the current position for collision and targeting
   */
  public getPosition(): THREE.Vector3 {
    return super.getPosition();
  }

  /**
   * Create a bounding volume for collision detection
   */
  protected createBoundingVolume(): void {
    // Create a box slightly smaller than the visual representation for better gameplay
    const halfExtents = new THREE.Vector3(
      this.baseScale.x * 0.9, // 90% of visual size
      this.baseScale.y * 0.9,
      this.baseScale.z * 0.9
    );
    
    // Create and set the bounding box
    const boundingBox = new BoundingBox(halfExtents, this.getPosition());
    this.setBoundingVolume(boundingBox);
  }

  /**
   * Set random movement direction and speed
   */
  public setRandomMovement(): void {
    // Random direction
    const angle = Math.random() * Math.PI * 2;
    const vx = Math.cos(angle) * this.movementSpeed;
    const vz = Math.sin(angle) * this.movementSpeed;
    
    // Set velocity
    this.setVelocity(vx, 0, vz);
  }
  
  /**
   * Update the direction to head toward the bread loaf
   * This implements a simple pathfinding approach
   */
  public updatePathfindingDirection(avoidObstacle: boolean = false): void {
    // Get the game state
    const gameState = GameState.getInstance();
    
    // If no loaf is placed, just move randomly
    if (!gameState.isLoafPlaced()) {
      this.setRandomMovement();
      return;
    }
    
    // Get the current position and the target position
    const currentPosition = this.getPosition();
    this.loafTargetPosition = gameState.getLoafPosition();
    
    // Calculate direction vector to target
    this.pathfindingDirection.subVectors(this.loafTargetPosition, currentPosition).normalize();
    
    // Add some randomness when avoiding obstacles
    if (avoidObstacle) {
      // Add a random deviation to the pathfinding direction
      const randomAngle = (Math.random() - 0.5) * Math.PI * 0.75; // Up to +/- 67.5 degrees
      const cos = Math.cos(randomAngle);
      const sin = Math.sin(randomAngle);
      
      // Rotate the direction vector around Y axis
      const newX = this.pathfindingDirection.x * cos - this.pathfindingDirection.z * sin;
      const newZ = this.pathfindingDirection.x * sin + this.pathfindingDirection.z * cos;
      
      this.pathfindingDirection.x = newX;
      this.pathfindingDirection.z = newZ;
    }
    
    // Set velocity based on direction and speed
    this.setVelocity(
      this.pathfindingDirection.x * this.movementSpeed,
      0,
      this.pathfindingDirection.z * this.movementSpeed
    );
  }

  /**
   * Handle LOD level changes
   * @param level The new LOD level
   */
  public setLODLevel(level: LODLevel): void {
    // Only update if the level changed
    if (this.activeDetailLevel === level) {
      return;
    }
    
    this.activeDetailLevel = level;
    const mesh = this.getMesh();
    
    if (mesh) {
      // Update geometry based on LOD level
      switch (level) {
        case LODLevel.HIGH:
          (mesh as THREE.Mesh).geometry = this.highDetailGeometry;
          break;
        case LODLevel.MEDIUM:
          (mesh as THREE.Mesh).geometry = this.mediumDetailGeometry;
          break;
        case LODLevel.LOW:
          (mesh as THREE.Mesh).geometry = this.lowDetailGeometry;
          break;
      }
    }
  }
  
  /**
   * Get the current LOD level
   */
  public getLODLevel(): LODLevel {
    return this.activeDetailLevel;
  }

  /**
   * Handle collision with another object
   * Now with loaf collision detection
   * @param currentTime The current game time
   * @param collidedObject The object collided with
   */
  public handleCollision(currentTime: number, collidedObject?: GameObject): void {
    // Implement cooldown to prevent rapid direction changes
    if (currentTime - this.lastCollisionTime < this.collisionCooldown) {
      return;
    }
    
    this.lastCollisionTime = currentTime;
    
    // Check if the collision is with the bread loaf (goal)
    if (collidedObject && collidedObject.getType() === ObjectType.STATIC) {
      const staticObject = collidedObject as any; // Cast to access potential static object type
      
      // Check if this is the loaf (by type or name if type is not available)
      if ((staticObject.getStaticType && staticObject.getStaticType() === StaticObjectType.LOAF) || 
          collidedObject.getName().includes('Loaf')) {
        // We've reached the bread loaf!
        this.reachGoal();
        return;
      }
    }
    
    // For other collisions, update pathfinding with avoidance behavior
    this.updatePathfindingDirection(true);
  }

  /**
   * Set active state of the toaster
   * @param active Whether the toaster is active
   */
  public setActive(active: boolean): void {
    this.activeState = active;
    
    console.log(`Setting toaster ${this.getId()} active state to ${active}`);
    
    // Use GameObject's built-in activation methods
    if (active) {
      this.activate(); // This will set the mesh visible if this.isVisible is true
      
      // Register with collision system
      CollisionSystem.getInstance().registerObject(this);
      
      // No need to set position, visibility, or initialize pathfinding here
      // These will be done by prepareForUse when the toaster is placed
      
      console.log(`Toaster ${this.getId()} activated`);
    } else {
      this.deactivate();
      
      // Update visibility
      const mesh = this.getMesh();
      if (mesh) {
        mesh.visible = false;
      }
      
      // Unregister from collision system
      CollisionSystem.getInstance().unregisterObject(this);
      
      // Reset state when deactivated
      this.resetState();
    }
  }
  
  /**
   * Check if toaster is active
   */
  public isActiveState(): boolean {
    return this.activeState;
  }
  
  /**
   * Reset the toaster's state for reuse
   */
  public resetState(): void {
    console.log(`Resetting toaster ${this.getId()} state for reuse`);
    
    // Reset variables
    this.reachedTarget = false;
    this.goalReached = false;
    this.isDead = false;
    
    // Reset health
    this.currentHealth = this.maxHealth;
    
    // Reset velocity
    this.setVelocity(0, 0, 0);
    
    // Reset callbacks
    this.onGoalReachedCallback = null;
    this.onDestroyCallback = null;
    
    // Reset timing
    this.lastCollisionTime = 0;
    this.lastPathUpdateTime = 0;
    
    // Reset collision cooldown
    this.collisionCooldown = 0.5;
    
    // Reset color
    const mesh = this.getMesh();
    if (mesh) {
      const material = (mesh as THREE.Mesh).material = this.material.clone();
      material.opacity = 1.0;
      material.transparent = false;
    } else {
      console.error(`No mesh found for toaster ${this.getId()} during reset`);
    }
    
    console.log(`Toaster ${this.getId()} state reset completed`);
  }
  
  /**
   * Prepares a toaster for active use in the game
   * This method should be called when retrieving a toaster from the pool
   * @param x X-coordinate position
   * @param z Z-coordinate position
   */
  public prepareForUse(x: number, z: number): void {
    // Set position - ensure it's slightly above ground
    this.setPosition(x, 0.5, z);
    
    // Ensure mesh is visible
    const mesh = this.getMesh();
    if (mesh) {
      mesh.visible = true;
    } else {
      console.error(`No mesh found for toaster ${this.getId()} during preparation`);
    }
    
    // Initialize pathfinding
    this.updatePathfindingDirection();
    
    console.log(`Prepared toaster ${this.getId()} for use at position (${x.toFixed(2)}, 0.5, ${z.toFixed(2)})`);
  }
  
  /**
   * Set callback for when toaster reaches goal
   */
  public setOnGoalReachedCallback(callback: () => void): void {
    this.onGoalReachedCallback = callback;
  }
  
  /**
   * Set callback for when toaster is destroyed
   */
  public setOnDestroyCallback(callback: () => void): void {
    this.onDestroyCallback = callback;
  }
  
  /**
   * Toaster has reached the goal (bread loaf)
   */
  public reachGoal(): void {
    // Only trigger once
    if (this.goalReached) {
      return;
    }
    
    this.goalReached = true;
    this.reachedTarget = true;
    
    // Stop moving
    this.setVelocity(0, 0, 0);
    // The movement state will be set to IDLE internally when velocity is zero
    
    // Change color to indicate success
    const mesh = this.getMesh();
    if (mesh) {
      (mesh as THREE.Mesh).material = new THREE.MeshStandardMaterial({
        color: 0x00FF00, // Green color
        roughness: 0.7,
        metalness: 0.2
      });
    }
    
    // Trigger callback
    if (this.onGoalReachedCallback) {
      this.onGoalReachedCallback();
    }
    
    console.log(`${this.getName()} has reached the bread loaf!`);
  }

  /**
   * Override update to handle pathfinding, bouncing off terrain boundaries and collision response
   */
  public override update(deltaTime: number): void {
    // Skip update if inactive
    if (!this.activeState) {
      return;
    }
    
    // First let parent class handle basic movement
    super.update(deltaTime);
    
    // If we already reached the target, nothing else to do
    if (this.reachedTarget) {
      return;
    }
    
    // Update pathfinding periodically for more direct routes
    const currentTime = Date.now() / 1000; // Current time in seconds
    if (currentTime - this.lastPathUpdateTime > this.pathfindingCooldown && 
        GameState.getInstance().isLoafPlaced()) {
      
      this.lastPathUpdateTime = currentTime;
      this.updatePathfindingDirection();
    }

    // If we're at the edge of the terrain, take special action
    if (this.isDirtyState()) {
      const position = this.getPosition();
      const velocity = this.getVelocity();
      
      // Check if we're at terrain boundaries and bounce
      const terrainSize = 500; // Should come from Config
      const halfSize = terrainSize / 2;
      const buffer = 5; // Buffer zone for smoother boundary response
      
      // Horizontal boundary checking (X-axis)
      if (position.x > halfSize - buffer) {
        // Too far right, reverse direction
        this.setVelocity(-Math.abs(velocity.x), velocity.y, velocity.z);
      } else if (position.x < -halfSize + buffer) {
        // Too far left, reverse direction
        this.setVelocity(Math.abs(velocity.x), velocity.y, velocity.z);
      }
      
      // Depth boundary checking (Z-axis)
      if (position.z > halfSize - buffer) {
        // Too far forward, reverse direction
        this.setVelocity(velocity.x, velocity.y, -Math.abs(velocity.z));
      } else if (position.z < -halfSize + buffer) {
        // Too far back, reverse direction
        this.setVelocity(velocity.x, velocity.y, Math.abs(velocity.z));
      }
      
      // Clear dirty state
      this.resetDirtyFlag();
    }
  }

  /**
   * Get the current velocity of this toaster
   * Used by towers for lead targeting
   */
  public getVelocity(): THREE.Vector3 {
    // Return a copy of the pathfinding direction scaled by movement speed
    return this.pathfindingDirection.clone().multiplyScalar(this.movementSpeed);
  }
} 